import type {
  AiCallsQuery,
  AiUsageQuery,
  ApiClient,
  CollectionPatch,
  DocumentPatch,
  DocumentQuery,
  SculptRunRequest,
} from "~/api/client";
import {
  ForbiddenError,
  NotFoundError,
  SculptConfirmRequiredError,
  SculptError,
  UnauthorizedError,
} from "~/api/errors";
import { createFakeAiAdmin } from "~/api/fakeAiAdmin";
import { createFakeAutomation } from "~/api/fakeAutomation";
import { createFakeCanopy, createFakeKnowledgeDebug } from "~/api/fakeCanopy";
import { createFakeCommand } from "~/api/fakeCommand";
import { createFakeComments } from "~/api/fakeComments";
import { createFakeConversations } from "~/api/fakeConversations";
import { createFakeEditor } from "~/api/fakeEditor";
import { createFakeGovernance } from "~/api/fakeGovernance";
import { createFakeGrants } from "~/api/fakeGrants";
import { createFakeIde } from "~/api/fakeIde";
import { createFakeIntents } from "~/api/fakeIntents";
import { createFakeKnowledge } from "~/api/fakeKnowledge";
import { createFakeLibrary } from "~/api/fakeLibrary";
import { createFakeMailReading } from "~/api/fakeMailReading";
import { createFakeMerge } from "~/api/fakeMerge";
import { createFakeNotifications } from "~/api/fakeNotifications";
import { createFakeOps } from "~/api/fakeOps";
import { createFakeProfiles } from "~/api/fakeProfiles";
import { createFakeReasoning } from "~/api/fakeReasoning";
import { createFakeResearch } from "~/api/fakeResearch";
import { createFakeWisdom } from "~/api/fakeWisdom";
import { createFakeWorkBench } from "~/api/fakeWorkBench";
import { createFakeWorkLineage } from "~/api/fakeWorkLineage";
import { createFakeWorkNow } from "~/api/fakeWorkNow";
import { createFakeWorkPlan } from "~/api/fakeWorkPlan";
import { createFakeWorkTodo } from "~/api/fakeWorkTodo";
import { createFakeGit } from "~/api/knowitGit";
import { createFakeMail } from "~/api/knowitMail";
import { createKnowItWork } from "~/api/knowitWork";
import type { CollectionPermission } from "~/api/libraryTypes";
import {
  ABILITY,
  type Actor,
  type AiCall,
  type AiCallsPage,
  type AiUsage,
  type ApiKey,
  type Collection,
  type Document,
  documentStatus,
  type Group,
  type Member,
  type PublicProfileCv,
  type QueueDigest,
  type Revision,
  type SculptLatest,
  type SculptOutcome,
  type SculptStreamEvent,
  type SculptThinker,
  type Session,
  type Share,
  type Viewer,
  type Workspace,
} from "~/api/types";
import { createKnowItResearch } from "./knowitResearch";

const SESSION_STORAGE_KEY = "slate.session";

/** Fixed timestamps — a seeded fixture must not drift with the clock between test runs. */
const BASE_TIME = Date.parse("2026-08-12T09:00:00.000Z");
const hoursAgo = (h: number) => new Date(BASE_TIME - h * 3600_000).toISOString();

const ADA: Actor = { id: "user-1", name: "Ada Lovelace", avatarUrl: null };
const GRACE: Actor = { id: "user-2", name: "Grace Hopper", avatarUrl: null };

const VIEWER: Viewer = {
  id: ADA.id,
  name: ADA.name,
  email: "ada@example.com",
  avatarUrl: null,
  language: "en",
  role: "admin",
};

const WORKSPACES: Workspace[] = [
  {
    id: "ws-acme",
    name: "Acme",
    subdomain: "acme",
    avatarUrl: null,
    current: true,
    suspended: false,
    autoEnter: false,
    lastSelectedAt: hoursAgo(1),
    // W4 §E-5: the preference document, seeded with a key that has nothing to do with IDE
    // grants — a toggle that erases it is exactly the defect the merge exists to prevent, and
    // `fake.test.ts` asserts this key survives.
    preferences: { notificationsDigestHour: 9 },
    // Viewer is an admin here: every gated surface is reachable.
    abilities: {
      [ABILITY.workspaceUpdate]: true,
      [ABILITY.workspaceManageMembers]: true,
      [ABILITY.workspaceCreateDocument]: true,
      [ABILITY.workspaceCreateCollection]: true,
      [ABILITY.workspaceManageApiKeys]: true,
      [ABILITY.workspaceManageIntegrations]: true,
      [ABILITY.workspaceViewAudit]: true,
    },
  },
  {
    id: "ws-readonly",
    name: "Readonly Co",
    subdomain: "readonly",
    avatarUrl: null,
    current: false,
    suspended: false,
    autoEnter: false,
    lastSelectedAt: null,
    preferences: {},
    abilities: {},
  },
];

function seedCollections(): Collection[] {
  return [
    {
      id: "col-product",
      name: "Product",
      emoji: "🚀",
      description: "Specs, plans and decisions.",
      index: 0,
      // Grouped under proj-atlas — proves the sidebar's project tree and "Remove from project".
      projectId: "proj-atlas",
      archivedAt: null,
      abilities: {
        [ABILITY.collectionUpdate]: true,
        [ABILITY.collectionDelete]: true,
        [ABILITY.collectionCreateDocument]: true,
      },
    },
    {
      id: "col-engineering",
      name: "Engineering",
      emoji: "🛠️",
      description: "Runbooks, architecture, incident reviews.",
      index: 1,
      projectId: null,
      archivedAt: null,
      abilities: {
        [ABILITY.collectionUpdate]: true,
        [ABILITY.collectionDelete]: false,
        [ABILITY.collectionCreateDocument]: true,
      },
    },
    {
      id: "col-company",
      name: "Company",
      emoji: "🏛️",
      description: "Policies and handbook. Read-only fixture.",
      index: 2,
      projectId: null,
      archivedAt: null,
      // Locked fixture: proves collection-level gating without a server.
      abilities: {},
    },
  ];
}

interface Seed {
  id: string;
  title: string;
  emoji: string | null;
  summary: string;
  collectionId: string | null;
  parentDocumentId?: string;
  updated: number;
  published?: boolean;
  archived?: boolean;
  deleted?: boolean;
  locked?: boolean;
  by?: Actor;
}

const SEEDS: Seed[] = [
  {
    id: "doc-1",
    title: "Quarterly plan",
    emoji: "🗓️",
    summary: "Objectives and the three bets for the quarter.",
    collectionId: "col-product",
    updated: 1,
    published: true,
  },
  {
    id: "doc-2",
    title: "Onboarding checklist",
    emoji: "✅",
    summary: "Everything a new joiner needs in week one.",
    collectionId: "col-product",
    updated: 3,
    published: true,
  },
  {
    id: "doc-3",
    title: "Pricing experiments",
    emoji: "💸",
    summary: "Nested under the quarterly plan.",
    collectionId: "col-product",
    parentDocumentId: "doc-1",
    updated: 4,
    published: true,
  },
  {
    id: "doc-4",
    title: "Incident review — auth outage",
    emoji: "🔥",
    summary: "Timeline, root cause and the two follow-ups.",
    collectionId: "col-engineering",
    updated: 9,
    published: true,
    by: GRACE,
  },
  {
    id: "doc-5",
    title: "Runbook: deploys",
    emoji: null,
    summary: "How to ship, and how to roll back.",
    collectionId: "col-engineering",
    updated: 26,
    published: true,
  },
  {
    id: "doc-6",
    title: "Architecture decision log",
    emoji: "🧭",
    summary: "One entry per irreversible decision.",
    collectionId: "col-engineering",
    updated: 50,
    published: true,
    by: GRACE,
  },
  {
    id: "doc-7",
    title: "Company handbook",
    emoji: "📘",
    summary: "Read-only fixture — no update ability.",
    collectionId: "col-company",
    updated: 74,
    published: true,
    locked: true,
  },
  {
    id: "doc-8",
    title: "Compliance policy",
    emoji: "📜",
    summary: "Read-only fixture — no update ability.",
    collectionId: "col-company",
    updated: 200,
    published: true,
    locked: true,
  },
  {
    id: "doc-9",
    title: "Untitled draft",
    emoji: null,
    summary: "Never published — appears only in Drafts.",
    collectionId: null,
    updated: 2,
  },
  {
    id: "doc-10",
    title: "Notes on the rename",
    emoji: "✏️",
    summary: "Never published — appears only in Drafts.",
    collectionId: "col-product",
    updated: 11,
  },
  {
    id: "doc-11",
    title: "Old roadmap (2025)",
    emoji: "🗃️",
    summary: "Archived — out of the way, still searchable.",
    collectionId: "col-product",
    updated: 900,
    published: true,
    archived: true,
  },
  {
    id: "doc-12",
    title: "Deprecated runbook",
    emoji: "🗃️",
    summary: "Archived — superseded by the deploy runbook.",
    collectionId: "col-engineering",
    updated: 1200,
    published: true,
    archived: true,
  },
  {
    id: "doc-13",
    title: "Scratch pad",
    emoji: "🗑️",
    summary: "In the trash — restorable.",
    collectionId: null,
    updated: 300,
    published: true,
    deleted: true,
  },
  {
    id: "doc-14",
    title: "Duplicate meeting notes",
    emoji: "🗑️",
    summary: "In the trash — restorable.",
    collectionId: "col-product",
    updated: 400,
    published: true,
    deleted: true,
  },
];

function seedDocuments(): Document[] {
  return SEEDS.map((seed) => ({
    id: seed.id,
    title: seed.title,
    emoji: seed.emoji,
    summary: seed.summary,
    // A believable markdown body: the platform's documents open with an H1 of the title.
    text: `# ${seed.title}\n\n${seed.summary}`,
    collectionId: seed.collectionId,
    parentDocumentId: seed.parentDocumentId ?? null,
    createdAt: hoursAgo(seed.updated + 24),
    updatedAt: hoursAgo(seed.updated),
    publishedAt: seed.published ? hoursAgo(seed.updated) : null,
    archivedAt: seed.archived ? hoursAgo(seed.updated) : null,
    deletedAt: seed.deleted ? hoursAgo(seed.updated) : null,
    createdBy: seed.by ?? ADA,
    views: (seed.title.length * 7) % 43,
    // W6: `doc-2` is the fixture on the SECOND editing lane, so the lane switch and the scene's
    // lane branch both have a real document in each state without a test opting one in first.
    editorLane: seed.id === "doc-2" ? ("v2" as const) : ("v1" as const),
    abilities: {
      [ABILITY.documentUpdate]: !seed.locked,
      [ABILITY.documentDelete]: !seed.locked && !seed.deleted,
      [ABILITY.documentShare]: !seed.locked && !seed.deleted,
      [ABILITY.documentArchive]: !seed.locked && !seed.archived && !seed.deleted,
      [ABILITY.documentRestore]: Boolean(seed.archived || seed.deleted),
      // W8: a locked document can still be COMMENTED on, which is what makes `doc-7` the
      // fixture that proves the split — turns land, every structural write refuses.
      [ABILITY.documentRead]: true,
      [ABILITY.documentComment]: !seed.deleted,
    },
  }));
}

const MEMBERS: Member[] = [
  {
    id: "user-1",
    name: "Ada Lovelace",
    email: "ada@example.com",
    avatarUrl: null,
    role: "admin",
    suspended: false,
    lastActiveAt: hoursAgo(1),
    abilities: { update: true, delete: false },
  },
  {
    id: "user-2",
    name: "Grace Hopper",
    email: "grace@example.com",
    avatarUrl: null,
    role: "member",
    suspended: false,
    lastActiveAt: hoursAgo(5),
    abilities: { update: true, delete: true },
  },
  {
    id: "user-3",
    name: "Alan Turing",
    email: "alan@example.com",
    avatarUrl: null,
    role: "member",
    suspended: false,
    lastActiveAt: hoursAgo(30),
    abilities: { update: true, delete: true },
  },
  {
    id: "user-4",
    name: "Katherine Johnson",
    email: "katherine@example.com",
    avatarUrl: null,
    role: "viewer",
    suspended: false,
    lastActiveAt: hoursAgo(100),
    abilities: { update: true, delete: true },
  },
  {
    id: "user-5",
    name: "Former Colleague",
    email: "former@example.com",
    avatarUrl: null,
    role: "member",
    suspended: true,
    lastActiveAt: hoursAgo(4000),
    abilities: { update: true, delete: true },
  },
];

const GROUPS: Group[] = [
  { id: "grp-eng", name: "Engineering", memberCount: 3, abilities: { update: true, delete: true } },
  { id: "grp-leads", name: "Leads", memberCount: 2, abilities: { update: true, delete: true } },
  {
    id: "grp-everyone",
    name: "Everyone",
    memberCount: 5,
    abilities: { update: false, delete: false },
  },
];

function seedApiKeys(): ApiKey[] {
  return [
    {
      id: "key-1",
      name: "CI pipeline",
      partialSecret: "sl_live_…8f21",
      createdAt: hoursAgo(700),
      lastActiveAt: hoursAgo(2),
      abilities: { revoke: true },
    },
    {
      id: "key-2",
      name: "Local scripts",
      partialSecret: "sl_live_…c40a",
      createdAt: hoursAgo(2000),
      lastActiveAt: null,
      abilities: { revoke: true },
    },
  ];
}

function seedShares(): Share[] {
  return [
    {
      id: "shr-1",
      documentId: "doc-1",
      documentTitle: "Quarterly plan",
      url: "/s/shr-1",
      includeChildren: true,
      views: 42,
      createdAt: hoursAgo(20),
      createdBy: ADA,
      abilities: { revoke: true },
    },
    {
      id: "shr-2",
      documentId: "doc-4",
      documentTitle: "Incident review — auth outage",
      url: "/s/shr-2",
      includeChildren: false,
      views: 7,
      createdAt: hoursAgo(60),
      createdBy: GRACE,
      abilities: { revoke: true },
    },
  ];
}

const FAKE_THINKER: SculptThinker = {
  thinkerId: "thinker-1",
  thinkerName: "Slate Assistant",
  model: "fixture-model",
};

/** One sculpt row, mirroring the platform's `sculpt_ops` semantics the UI depends on. */
interface FakeSculptRow {
  opId: string;
  op: string;
  lane: "act" | "propose";
  status: string;
  idempotencyKey: string;
  proposal: { markdown: string; summary: string } | null;
  /** The document's text (and title) as they stood before the apply — the revert target. */
  checkpointText: string;
  checkpointTitle: string;
  postApplyText: string | null;
  createdAt: string;
  appliedSteps: number;
  totalSteps: number;
}

export interface FakeApiOptions {
  /** Simulated round-trip time. Tests pass 0; the app uses the default so loading states show. */
  latency?: number;
  /** Start signed in — used by tests that are not exercising the login flow. */
  authenticated?: boolean;
  persistSession?: boolean;
}

/**
 * In-memory implementation of the entire API contract. Mutations are checked against the
 * same `abilities` the UI reads, so a gating bug fails here too — no backend required.
 */
/**
 * The four starters, clean-roomed from `DocumentsController.Actions.cs:497-520` — the C#
 * controller is the source of truth for their content, so nothing from Outline's template
 * machinery is needed to use the endpoint. Kept as markdown because that is slate's document
 * body format; the server stores the same blocks as ProseMirror JSON plus this same text.
 */
const STARTER_TEMPLATES: ReadonlyArray<{ title: string; text: string }> = [
  {
    title: "Meeting Notes",
    text: "# Meeting notes\n\nDate:\n\nAttendees:\n\n## Agenda\n\n## Decisions\n\n## Action items\n\n- [ ] ",
  },
  {
    title: "Planning",
    text: "# Plan\n\n## Goal\n\n## Milestones\n\n## Owners\n\n## Timeline\n",
  },
  {
    title: "Brainstorm",
    text: "# Brainstorm\n\n## Topic\n\n## Ideas\n\n## Next steps\n",
  },
  {
    title: "Event / Picnic",
    text: "# Event plan\n\n## What & when\n\n## Who\u2019s coming\n\n## What to bring\n\n## Logistics\n",
  },
];

export class FakeApi implements ApiClient {
  /** The reasoning engine in memory (fakeReasoning.ts): `/reasoning` and the run tab work in fake mode. */
  reasoning = createFakeReasoning();
  private documentStore: Document[] = seedDocuments();
  private collectionStore: Collection[] = seedCollections();
  private apiKeyStore: ApiKey[] = seedApiKeys();
  private shareStore: Share[] = seedShares();
  // W5: MEMBERS/GROUPS were read-only module constants until this wave gave them writes.
  // Deep-cloned per instance — the module const stays the pristine seed, or one test's
  // `people.suspend()` would leak into the next test's fresh `new FakeApi()`.
  private memberStore: Member[] = MEMBERS.map((m) => this.clone(m));
  private groupStore: Group[] = GROUPS.map((g) => this.clone(g));
  private session: Session | null = null;
  private readonly latency: number;
  private readonly persistSession: boolean;
  private currentWorkspaceId = "ws-acme";

  /**
   * Work (Docit Work) has no offline tile corpus yet; fail honestly rather than fake tiles. The one
   * exception is the plan review (`work.plan.*`), whose fixture enforces `plan_changed`.
   */
  readonly workPlanFixture = createFakeWorkPlan();
  /** The now board (`work.now`) + answering its help question; everything else goes to the plan fixture. */
  readonly workNowFixture = createFakeWorkNow();
  /** Cancellation + lineage (`work.lineage`, `work.cancel`, `work.cancel.reconcile`). */
  readonly workLineageFixture = createFakeWorkLineage();
  /** The To-do dashboard (`work.todo`) + answering its questions (by key). */
  readonly workTodoFixture = createFakeWorkTodo();
  /** Bench (`work.bench.*`, `work.phased.*`, `work.phase.*`, `work.items.send`, `work.plan.rules`). */
  readonly workBenchFixture = createFakeWorkBench();
  /** The AI command bar + TL;DR (`ai.command.*`, `ai.tldr`) — rules, never a model. */
  readonly commandFixture = createFakeCommand();
  work = createKnowItWork({
    post: (method, body) =>
      this.commandFixture.handles(method)
        ? this.commandFixture.post(method, body)
        : this.workBenchFixture.handles(method)
          ? this.workBenchFixture.post(method, body)
          : this.workTodoFixture.handles(method, body)
            ? this.workTodoFixture.post(method, body)
            : this.workNowFixture.handles(method)
              ? this.workNowFixture.post(method, body)
              : this.workLineageFixture.handles(method)
                ? this.workLineageFixture.post(method, body)
                : this.workPlanFixture.post(method, body),
  });

  /** No repositories in the fixture corpus: lists empty, refuses to connect (see `createFakeGit`). */
  git = createFakeGit();

  /** No mail provider in the fixture corpus: nothing to connect (see `createFakeMail`). */
  mail = createFakeMail();

  /** The Mail area fixture: five read mails (one the Polish "bill unpaid" acceptance case), a to-do, insights, settings. */
  mailReading = createFakeMailReading();

  /**
   * The research projections have no fixture corpus; fail honestly rather than fake a graph.
   * The owner's part of a dialogue (role library, ask, continue) IS faked — `createFakeResearch`.
   */
  research = {
    ...createKnowItResearch({
      get: async () => {
        throw new Error("Research needs the real API.");
      },
      post: async () => {
        throw new Error("Research needs the real API.");
      },
    }),
    ...createFakeResearch(),
    post: async (): Promise<unknown> => {
      throw new Error("The research surfaces need the real API.");
    },
    upload: async (): Promise<unknown> => {
      throw new Error("The research surfaces need the real API.");
    },
  };

  constructor(options: FakeApiOptions = {}) {
    this.latency = options.latency ?? 50;
    this.persistSession = options.persistSession ?? true;
    if (options.authenticated) {
      this.session = {
        userId: VIEWER.id,
        workspaceId: this.currentWorkspaceId,
        createdAt: new Date(BASE_TIME).toISOString(),
      };
    } else if (this.persistSession) {
      this.session = this.readStoredSession();
    }
  }

  private readStoredSession(): Session | null {
    try {
      const raw = localStorage.getItem(SESSION_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  }

  private writeStoredSession(session: Session | null): void {
    if (!this.persistSession) {
      return;
    }
    try {
      if (session) {
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
      } else {
        localStorage.removeItem(SESSION_STORAGE_KEY);
      }
    } catch {
      // Persistence is best-effort; the in-memory session still works for this tab.
    }
  }

  private async delay<T>(value: T): Promise<T> {
    if (this.latency > 0) {
      await new Promise((resolve) => setTimeout(resolve, this.latency));
    }
    return value;
  }

  private requireSession(): Session {
    if (!this.session) {
      throw new UnauthorizedError();
    }
    return this.session;
  }

  /**
   * The workspace's preference document, per FakeApi instance. Module-level `WORKSPACES` is
   * shared, so mutating a seed there would bleed one test's toggle into the next test's fixture.
   */
  private preferencesStore: Record<string, unknown> = { notificationsDigestHour: 9 };

  private workspace(): Workspace {
    const found = WORKSPACES.find((w) => w.id === this.currentWorkspaceId);
    if (!found) {
      throw new NotFoundError("Workspace not found");
    }
    return found;
  }

  private findDocument(id: string): Document {
    const found = this.documentStore.find((d) => d.id === id);
    if (!found) {
      throw new NotFoundError("Document not found");
    }
    return found;
  }

  private requireDocumentAbility(id: string, ability: string, message: string): Document {
    const document = this.findDocument(id);
    if (!document.abilities[ability]) {
      throw new ForbiddenError(message);
    }
    return document;
  }

  private replaceDocument(next: Document): Document {
    this.documentStore = this.documentStore.map((d) => (d.id === next.id ? next : d));
    return { ...next, abilities: { ...next.abilities } };
  }

  // ── Document Sculptor ──────────────────────────────────────────────────────

  private sculptStore = new Map<string, FakeSculptRow[]>();

  private sculptRows(documentId: string): FakeSculptRow[] {
    return this.sculptStore.get(documentId) ?? [];
  }

  private newestSculpt(documentId: string): FakeSculptRow | null {
    return this.sculptRows(documentId)[0] ?? null;
  }

  /** Same fingerprint idea as the platform's: any edit in between mints a different token. */
  private sculptConfirmToken(document: Document, op: string): string {
    return `confirm-${document.id}-${op}-${document.text.length}`;
  }

  private static firstProse(text: string): string {
    const line = text
      .split("\n")
      .map((l) => l.trim())
      .find((l) => l.length > 0 && !l.startsWith("#"));
    return line ? (line.length > 140 ? `${line.slice(0, 139)}…` : line) : "";
  }

  /** Deterministic "generation" — the fixture drafts a plausible document from instructions. */
  private static composeFixture(title: string, instructions: string): string {
    const topic = instructions.trim() || title;
    return [
      `# ${title}`,
      "",
      "## Overview",
      `${topic}`,
      "",
      "## Details",
      "A drafted section expanding on the request, written by the fixture sculptor.",
      "",
      "## Next steps",
      "- Review this draft",
      "- Refine any section with another sculpt",
    ].join("\n");
  }

  private applySculptText(document: Document, markdown: string): void {
    this.replaceDocument({
      ...document,
      text: markdown,
      summary: FakeApi.firstProse(markdown),
      updatedAt: new Date().toISOString(),
    });
  }

  private static sculptOutcome(row: FakeSculptRow): SculptOutcome {
    return {
      opId: row.opId,
      lane: row.lane,
      status: row.status,
      appliedSteps: row.appliedSteps,
      totalSteps: row.totalSteps,
      checkpointRevisionId: row.postApplyText === null ? null : `rev-${row.opId}`,
      proposal: row.proposal,
      thinker: { ...FAKE_THINKER },
      tokensUsed: 128,
    };
  }

  /**
   * The shared core of JSON and streaming sculpt. Mirrors the platform's decisions the UI
   * leans on: lane resolution, the destructive-confirm 409, idempotent replay, and the
   * proposal-vs-apply split.
   */
  private runSculpt(id: string, request: SculptRunRequest): FakeSculptRow {
    this.requireSession();
    const document = this.findDocument(id);

    const replay = this.sculptRows(id).find((r) => r.idempotencyKey === request.idempotencyKey);
    if (replay) {
      return replay;
    }

    const canUpdate = document.abilities[ABILITY.documentUpdate] === true;
    const lane: "act" | "propose" =
      canUpdate && request.requestedLane !== "propose" ? "act" : "propose";

    const destructive =
      request.op === "restructure" ||
      request.op === "mergeSections" ||
      (request.op === "compose" && document.text.trim().length > 0);
    if (lane === "act" && destructive) {
      const expected = this.sculptConfirmToken(document, request.op);
      if (request.confirmToken !== expected) {
        const sections = Math.max(1, document.text.split(/^##? /m).length - 1);
        throw new SculptConfirmRequiredError(expected, sections, `replaces ${sections} sections`);
      }
    }

    const provided =
      typeof request.params?.providedMarkdown === "string" ? request.params.providedMarkdown : null;
    const generated =
      provided ?? FakeApi.composeFixture(document.title, request.instructions ?? "");
    const nextText =
      request.op === "insertSection" || request.op === "writeSection"
        ? `${document.text.trimEnd()}\n\n## ${request.instructions?.slice(0, 40) || "New section"}\n\nA drafted section from the fixture sculptor.`.trim()
        : generated;

    const row: FakeSculptRow = {
      opId: `sculpt-${Math.random().toString(36).slice(2, 10)}`,
      op: request.op,
      lane,
      status: lane === "act" ? "applied" : "proposed",
      idempotencyKey: request.idempotencyKey,
      proposal:
        lane === "act"
          ? null
          : {
              markdown: nextText,
              summary: `${request.op}: ${(request.instructions ?? "").slice(0, 80) || "proposed change"}`,
            },
      checkpointText: document.text,
      checkpointTitle: document.title,
      postApplyText: lane === "act" ? nextText : null,
      createdAt: new Date().toISOString(),
      appliedSteps: lane === "act" ? 1 : 0,
      totalSteps: 1,
    };

    if (lane === "act") {
      this.applySculptText(document, nextText);
    }
    this.sculptStore.set(id, [row, ...this.sculptRows(id)]);
    return row;
  }

  private clone<T extends { abilities: Record<string, boolean> }>(value: T): T {
    return { ...value, abilities: { ...value.abilities } };
  }

  auth = {
    login: async (email: string): Promise<Session> => {
      const session: Session = {
        userId: VIEWER.id,
        workspaceId: this.currentWorkspaceId,
        createdAt: new Date().toISOString(),
      };
      this.session = session;
      this.writeStoredSession(session);
      // The email is accepted as-is: this fixture models a signed-in user, not an IdP.
      void email;
      return this.delay(session);
    },
    logout: async (): Promise<void> => {
      this.session = null;
      this.writeStoredSession(null);
      return this.delay(undefined);
    },
    session: async (): Promise<Session | null> => this.delay(this.session),
  };

  async viewer(): Promise<Viewer> {
    this.requireSession();
    return this.delay({ ...VIEWER });
  }

  workspaces = {
    list: async (): Promise<Workspace[]> => {
      this.requireSession();
      return this.delay(
        WORKSPACES.map((w) => ({
          ...this.clone(w),
          current: w.id === this.currentWorkspaceId,
          preferences:
            w.id === this.currentWorkspaceId ? { ...this.preferencesStore } : { ...w.preferences },
        })),
      );
    },
    current: async (): Promise<Workspace> => {
      this.requireSession();
      return this.delay({
        ...this.clone(this.workspace()),
        preferences: { ...this.preferencesStore },
      });
    },

    switch: async (workspaceId: string): Promise<void> => {
      this.requireSession();
      const target = WORKSPACES.find((w) => w.id === workspaceId);
      if (!target) {
        throw new NotFoundError("No such workspace");
      }
      if (target.suspended) {
        throw new ForbiddenError("That workspace is suspended");
      }
      this.currentWorkspaceId = workspaceId;
      this.preferencesStore = { ...(target.preferences as Record<string, unknown>) };
      return this.delay(undefined);
    },

    /**
     * W4 §E-5, enforced offline: this MERGES. `team.update` replaces the whole document
     * server-side, so a fake that let a bare patch through would let the defect ship — the
     * unrelated seeded key is what proves the merge in `fake.test.ts`.
     */
    updatePreferences: async (patch: Record<string, unknown>): Promise<Record<string, unknown>> => {
      this.requireSession();
      if (this.workspace().abilities[ABILITY.workspaceUpdate] !== true) {
        throw new ForbiddenError("You cannot change workspace settings");
      }
      this.preferencesStore = { ...this.preferencesStore, ...patch };
      return this.delay({ ...this.preferencesStore });
    },
  };

  collections = {
    /**
     * W10 §8 — archived collections are excluded unless asked for, exactly as the platform's
     * `statusFilter` works: asking includes BOTH, so the caller separates on `archivedAt`.
     */
    list: async (options: { includeArchived?: boolean } = {}): Promise<Collection[]> => {
      this.requireSession();
      return this.delay(
        [...this.collectionStore]
          .filter((collection) => (options.includeArchived ? true : collection.archivedAt === null))
          .sort((a, b) => a.index - b.index)
          .map((c) => this.clone(c)),
      );
    },

    get: async (id: string): Promise<Collection> => {
      this.requireSession();
      const found = this.collectionStore.find((c) => c.id === id);
      if (!found) {
        throw new NotFoundError("Collection not found");
      }
      return this.delay(this.clone(found));
    },

    create: async (data: { name: string }): Promise<Collection> => {
      this.requireSession();
      if (!this.workspace().abilities[ABILITY.workspaceCreateCollection]) {
        throw new ForbiddenError("You cannot create collections in this workspace");
      }
      const collection: Collection = {
        id: `col-${this.collectionStore.length + 1}`,
        name: data.name,
        emoji: null,
        description: "",
        index: this.collectionStore.length,
        projectId: null,
        archivedAt: null,
        abilities: {
          [ABILITY.collectionUpdate]: true,
          [ABILITY.collectionDelete]: true,
          [ABILITY.collectionCreateDocument]: true,
        },
      };
      this.collectionStore = [...this.collectionStore, collection];
      return this.delay(this.clone(collection));
    },

    update: async (id: string, data: CollectionPatch): Promise<Collection> => {
      this.requireSession();
      const collection = await this.collections.get(id);
      if (!collection.abilities[ABILITY.collectionUpdate]) {
        throw new ForbiddenError("You cannot edit this collection");
      }
      const next: Collection = {
        ...collection,
        ...(data.name === undefined ? {} : { name: data.name }),
        ...(data.emoji === undefined ? {} : { emoji: data.emoji }),
        ...(data.description === undefined ? {} : { description: data.description }),
      };
      this.collectionStore = this.collectionStore.map((c) => (c.id === id ? next : c));
      return this.delay(this.clone(next));
    },

    delete: async (id: string): Promise<void> => {
      this.requireSession();
      const collection = await this.collections.get(id);
      if (!collection.abilities[ABILITY.collectionDelete]) {
        throw new ForbiddenError("You cannot delete this collection");
      }
      this.collectionStore = this.collectionStore.filter((c) => c.id !== id);
      return this.delay(undefined);
    },

    /**
     * W10 §8. The server requires `Delete` on the collection for BOTH directions, deliberately:
     * archiving hides every document inside it, so it is a destructive-class operation and not
     * an edit.
     */
    archive: async (id: string): Promise<Collection> => {
      this.requireSession();
      const collection = await this.collections.get(id);
      if (!collection.abilities[ABILITY.collectionDelete]) {
        throw new ForbiddenError("You cannot archive this collection");
      }
      const next: Collection = { ...collection, archivedAt: new Date().toISOString() };
      this.collectionStore = this.collectionStore.map((c) => (c.id === id ? next : c));
      return this.delay(this.clone(next));
    },

    restore: async (id: string): Promise<Collection> => {
      this.requireSession();
      const collection = await this.collections.get(id);
      if (!collection.abilities[ABILITY.collectionDelete]) {
        throw new ForbiddenError("You cannot restore this collection");
      }
      const next: Collection = { ...collection, archivedAt: null };
      this.collectionStore = this.collectionStore.map((c) => (c.id === id ? next : c));
      return this.delay(this.clone(next));
    },

    people: {
      list: (collectionId: string) => this.libraryApi.collectionPeople.list(collectionId),
      add: (input: { collectionId: string; userId: string; permission: CollectionPermission }) =>
        this.libraryApi.collectionPeople.add(input),
      remove: (input: { collectionId: string; userId: string }) =>
        this.libraryApi.collectionPeople.remove(input),
      groups: (collectionId: string) => this.libraryApi.collectionPeople.groups(collectionId),
      addGroup: (input: {
        collectionId: string;
        groupId: string;
        permission: CollectionPermission;
      }) => this.libraryApi.collectionPeople.addGroup(input),
      removeGroup: (input: { collectionId: string; groupId: string }) =>
        this.libraryApi.collectionPeople.removeGroup(input),
    },
  };

  documents = {
    list: async (query: DocumentQuery = {}): Promise<Document[]> => {
      this.requireSession();
      const needle = query.query?.trim().toLowerCase();
      const status = query.status ?? "published";

      const result = this.documentStore
        .filter((document) => documentStatus(document) === status)
        .filter((d) => (query.collectionId ? d.collectionId === query.collectionId : true))
        .filter((d) =>
          query.parentDocumentId === undefined
            ? true
            : d.parentDocumentId === query.parentDocumentId,
        )
        .filter((d) =>
          needle
            ? d.title.toLowerCase().includes(needle) || d.summary.toLowerCase().includes(needle)
            : true,
        )
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, query.limit ?? 100)
        .map((d) => this.clone(d));

      return this.delay(result);
    },

    get: async (id: string): Promise<Document> => {
      this.requireSession();
      return this.delay(this.clone(this.findDocument(id)));
    },

    create: async (data: {
      title: string;
      collectionId?: string | null;
      /** W7: `create-document` and the starter templates both create WITH a body. */
      text?: string;
      publish?: boolean;
    }): Promise<Document> => {
      this.requireSession();
      if (!this.workspace().abilities[ABILITY.workspaceCreateDocument]) {
        throw new ForbiddenError("You cannot create documents in this workspace");
      }
      const now = new Date().toISOString();
      const body = data.text ?? "";
      const document: Document = {
        id: `doc-${this.documentStore.length + 1}-${Math.random().toString(36).slice(2, 8)}`,
        title: data.title,
        emoji: null,
        summary: FakeApi.firstProse(body),
        text: body,
        collectionId: data.collectionId ?? null,
        parentDocumentId: null,
        createdAt: now,
        updatedAt: now,
        publishedAt: null,
        archivedAt: null,
        deletedAt: null,
        createdBy: ADA,
        views: 0,
        // A new document is always on the markdown lane; the second lane is opted into.
        editorLane: "v1",
        abilities: {
          [ABILITY.documentUpdate]: true,
          [ABILITY.documentDelete]: true,
          [ABILITY.documentShare]: true,
          [ABILITY.documentArchive]: true,
          [ABILITY.documentRestore]: false,
        },
      };
      this.documentStore = [document, ...this.documentStore];
      return this.delay(this.clone(document));
    },

    update: async (id: string, data: DocumentPatch): Promise<Document> => {
      this.requireSession();
      const document = this.requireDocumentAbility(
        id,
        ABILITY.documentUpdate,
        "You cannot edit this document",
      );
      return this.delay(
        this.replaceDocument({
          ...document,
          ...(data.title === undefined ? {} : { title: data.title }),
          ...(data.emoji === undefined ? {} : { emoji: data.emoji }),
          ...(data.summary === undefined ? {} : { summary: data.summary }),
          ...(data.collectionId === undefined ? {} : { collectionId: data.collectionId }),
          updatedAt: new Date().toISOString(),
        }),
      );
    },

    publish: async (id: string): Promise<Document> => {
      this.requireSession();
      const document = this.requireDocumentAbility(
        id,
        ABILITY.documentUpdate,
        "You cannot publish this document",
      );
      return this.delay(
        this.replaceDocument({ ...document, publishedAt: new Date().toISOString() }),
      );
    },

    /**
     * W10 §1 — the server needs `Update` on the DOCUMENT *and* `Update` on the DESTINATION
     * collection (`DocumentsController.Actions.cs` R5), so both are enforced here: a fake that
     * only checked the document would let a gating regression through green tests.
     */
    move: async (input: {
      id: string;
      collectionId?: string;
      parentDocumentId?: string;
    }): Promise<Document> => {
      this.requireSession();
      const document = this.requireDocumentAbility(
        input.id,
        ABILITY.documentUpdate,
        "You cannot move this document",
      );
      if (input.collectionId !== undefined) {
        const destination = this.collectionStore.find((c) => c.id === input.collectionId);
        if (!destination) {
          throw new NotFoundError("Collection not found");
        }
        if (!destination.abilities[ABILITY.collectionUpdate]) {
          throw new ForbiddenError("You cannot move documents into that collection");
        }
      }
      if (input.parentDocumentId !== undefined) {
        if (input.parentDocumentId === input.id) {
          throw new ForbiddenError("A document cannot be its own parent");
        }
        this.findDocument(input.parentDocumentId);
      }
      return this.delay(
        this.replaceDocument({
          ...document,
          // Omitted means "leave it", never "clear it" — the server's two independent ifs.
          ...(input.collectionId === undefined ? {} : { collectionId: input.collectionId }),
          ...(input.parentDocumentId === undefined
            ? {}
            : { parentDocumentId: input.parentDocumentId }),
          updatedAt: new Date().toISOString(),
        }),
      );
    },

    /**
     * W10 §4. The server gates unpublish on `Update`, not on `publish` — an unpublish is a
     * mutation of the document, and the `publish` ability governs the other direction.
     */
    unpublish: async (id: string): Promise<Document> => {
      this.requireSession();
      const document = this.requireDocumentAbility(
        id,
        ABILITY.documentUpdate,
        "You cannot unpublish this document",
      );
      return this.delay(
        this.replaceDocument({
          ...document,
          publishedAt: null,
          updatedAt: new Date().toISOString(),
        }),
      );
    },

    archive: async (id: string): Promise<Document> => {
      this.requireSession();
      const document = this.requireDocumentAbility(
        id,
        ABILITY.documentArchive,
        "You cannot archive this document",
      );
      return this.delay(
        this.replaceDocument({
          ...document,
          archivedAt: new Date().toISOString(),
          abilities: {
            ...document.abilities,
            [ABILITY.documentArchive]: false,
            [ABILITY.documentRestore]: true,
          },
        }),
      );
    },

    restore: async (id: string): Promise<Document> => {
      this.requireSession();
      const document = this.requireDocumentAbility(
        id,
        ABILITY.documentRestore,
        "You cannot restore this document",
      );
      return this.delay(
        this.replaceDocument({
          ...document,
          archivedAt: null,
          deletedAt: null,
          abilities: {
            ...document.abilities,
            [ABILITY.documentArchive]: true,
            [ABILITY.documentDelete]: true,
            [ABILITY.documentRestore]: false,
          },
        }),
      );
    },

    delete: async (id: string): Promise<void> => {
      this.requireSession();
      const document = this.requireDocumentAbility(
        id,
        ABILITY.documentDelete,
        "You cannot delete this document",
      );
      this.replaceDocument({
        ...document,
        deletedAt: new Date().toISOString(),
        abilities: {
          ...document.abilities,
          [ABILITY.documentDelete]: false,
          [ABILITY.documentRestore]: true,
        },
      });
      return this.delay(undefined);
    },

    permanentlyDelete: async (id: string): Promise<void> => {
      this.requireSession();
      const document = this.findDocument(id);
      if (documentStatus(document) !== "deleted") {
        throw new ForbiddenError("Only documents in the trash can be permanently deleted");
      }
      this.documentStore = this.documentStore.filter((d) => d.id !== id);
      return this.delay(undefined);
    },

    sculpt: async (id: string, request: SculptRunRequest): Promise<SculptOutcome> => {
      return this.delay(FakeApi.sculptOutcome(this.runSculpt(id, request)));
    },

    sculptStream: async (
      id: string,
      request: SculptRunRequest,
      onEvent: (event: SculptStreamEvent) => void,
    ): Promise<void> => {
      // Failable checks first, like the platform: a refusal is thrown, never framed.
      const row = this.runSculpt(id, request);
      const text = row.postApplyText ?? row.proposal?.markdown ?? "";

      onEvent({ type: "plan", totalSteps: row.totalSteps, appliedSteps: 0 });
      for (const chunk of text.split(/(?<=\n\n)/)) {
        await this.delay(undefined);
        onEvent({ type: "delta", text: chunk });
      }
      onEvent({
        type: row.status === "applied" ? "applied" : "proposal",
        opId: row.opId,
        appliedSteps: row.appliedSteps,
        totalSteps: row.totalSteps,
        ...(row.postApplyText === null ? {} : { checkpointRevisionId: `rev-${row.opId}` }),
      });
    },

    sculptLatest: async (id: string): Promise<SculptLatest | null> => {
      this.requireSession();
      const document = this.findDocument(id);
      const row = this.newestSculpt(id);
      if (!row) {
        return this.delay(null);
      }
      const revertible = row.status === "applied";
      return this.delay({
        opId: row.opId,
        op: row.op,
        lane: row.lane,
        status: row.status,
        proposal: row.proposal,
        revertible,
        revertMode: revertible
          ? row.postApplyText === document.text
            ? ("direct" as const)
            : ("propose" as const)
          : null,
        createdAt: row.createdAt,
        thinker: { ...FAKE_THINKER },
      });
    },

    sculptResolve: async (
      id: string,
      opId: string,
      action: "accept" | "reject" | "discard",
    ): Promise<SculptOutcome> => {
      this.requireSession();
      const document = this.findDocument(id);
      const row = this.sculptRows(id).find((r) => r.opId === opId);
      if (!row) {
        throw new NotFoundError("No such sculpt");
      }

      if (action === "accept") {
        if (document.abilities[ABILITY.documentUpdate] !== true) {
          throw new ForbiddenError("You cannot apply changes to this document");
        }
        if (row.op === "revertRestore") {
          // Mirrors the platform: a drift-minted restore row is reviewed, never accepted.
          throw new SculptError(
            "not_proposed",
            "This restore is reviewed via reject or discard",
            400,
          );
        }
        if (row.status === "proposed" || row.status === "degraded") {
          row.checkpointText = document.text;
          row.checkpointTitle = document.title;
          row.postApplyText = row.proposal?.markdown ?? document.text;
          this.applySculptText(document, row.postApplyText);
          row.status = "applied";
          row.appliedSteps = row.totalSteps;
        }
        return this.delay(FakeApi.sculptOutcome(row));
      }

      if (row.status === "proposed" || row.status === "degraded") {
        row.status = action === "reject" ? "rejected" : "discarded";
      }
      return this.delay(FakeApi.sculptOutcome(row));
    },

    sculptRevert: async (id: string, opId: string): Promise<SculptOutcome> => {
      this.requireSession();
      const document = this.findDocument(id);
      if (document.abilities[ABILITY.documentUpdate] !== true) {
        throw new ForbiddenError("You cannot revert changes to this document");
      }
      const row = this.sculptRows(id).find((r) => r.opId === opId);
      if (!row) {
        throw new NotFoundError("No such sculpt");
      }
      if (row.status === "reverted") {
        return this.delay(FakeApi.sculptOutcome(row));
      }
      const newest = this.newestSculpt(id);
      if (newest?.opId !== row.opId || row.status !== "applied") {
        throw new SculptError("not_latest_sculpt", "A newer sculpt exists", 409);
      }

      if (row.postApplyText === document.text) {
        this.replaceDocument({
          ...document,
          text: row.checkpointText,
          summary: FakeApi.firstProse(row.checkpointText),
          ...(row.op === "retitle" ? { title: row.checkpointTitle } : {}),
          updatedAt: new Date().toISOString(),
        });
        row.status = "reverted";
        return this.delay(FakeApi.sculptOutcome(row));
      }

      // Drifted: never blind-apply — stage the restore as a reviewable proposal instead.
      const proposalRow: FakeSculptRow = {
        opId: `sculpt-${Math.random().toString(36).slice(2, 10)}`,
        op: "revertRestore",
        lane: "propose",
        status: "proposed",
        idempotencyKey: `revert-${row.opId}`,
        proposal: {
          markdown: row.checkpointText,
          summary: `Restore to before sculpt '${row.op}' (the document has changed since then)`,
        },
        checkpointText: document.text,
        checkpointTitle: document.title,
        postApplyText: null,
        createdAt: new Date().toISOString(),
        appliedSteps: 0,
        totalSteps: 1,
      };
      this.sculptStore.set(id, [proposalRow, ...this.sculptRows(id)]);
      return this.delay(FakeApi.sculptOutcome(proposalRow));
    },

    revisions: async (id: string): Promise<Revision[]> => {
      this.requireSession();
      const document = this.findDocument(id);
      const revisions: Revision[] = [
        {
          id: `${id}-rev-3`,
          documentId: id,
          title: document.title,
          summary: "Tightened the summary.",
          createdAt: document.updatedAt,
          createdBy: ADA,
        },
        {
          id: `${id}-rev-2`,
          documentId: id,
          title: document.title,
          summary: "Added the follow-ups section.",
          createdAt: hoursAgo(120),
          createdBy: GRACE,
        },
        {
          id: `${id}-rev-1`,
          documentId: id,
          title: document.title,
          summary: "Created.",
          createdAt: document.createdAt,
          createdBy: document.createdBy,
        },
      ];
      return this.delay(revisions);
    },
  };

  async search(query: string): Promise<Document[]> {
    return this.documents.list({ query });
  }

  members = {
    list: async (): Promise<Member[]> => {
      this.requireSession();
      return this.delay(this.memberStore.map((m) => this.clone(m)));
    },
  };

  groups = {
    list: async (): Promise<Group[]> => {
      this.requireSession();
      return this.delay(this.groupStore.map((g) => this.clone(g)));
    },
  };

  apiKeys = {
    list: async (): Promise<ApiKey[]> => {
      this.requireSession();
      return this.delay(this.apiKeyStore.map((k) => this.clone(k)));
    },
    create: async (data: { name: string }): Promise<ApiKey> => {
      this.requireSession();
      if (!this.workspace().abilities[ABILITY.workspaceManageApiKeys]) {
        throw new ForbiddenError("You cannot manage API keys in this workspace");
      }
      const key: ApiKey = {
        id: `key-${this.apiKeyStore.length + 1}`,
        name: data.name,
        partialSecret: `sl_live_…${Math.random().toString(36).slice(2, 6)}`,
        createdAt: new Date().toISOString(),
        lastActiveAt: null,
        abilities: { revoke: true },
      };
      this.apiKeyStore = [...this.apiKeyStore, key];
      return this.delay(this.clone(key));
    },
    revoke: async (id: string): Promise<void> => {
      this.requireSession();
      if (!this.workspace().abilities[ABILITY.workspaceManageApiKeys]) {
        throw new ForbiddenError("You cannot manage API keys in this workspace");
      }
      this.apiKeyStore = this.apiKeyStore.filter((k) => k.id !== id);
      return this.delay(undefined);
    },
  };

  shares = {
    list: async (): Promise<Share[]> => {
      this.requireSession();
      return this.delay(this.shareStore.map((s) => this.clone(s)));
    },
    revoke: async (id: string): Promise<void> => {
      this.requireSession();
      this.shareStore = this.shareStore.filter((s) => s.id !== id);
      return this.delay(undefined);
    },
  };

  /**
   * Flows, runs, schedules, agents and activity — with the same gating the real server applies,
   * so a permission bug in the automation surface fails offline too.
   */
  automation = createFakeAutomation({
    requireSession: () => this.requireSession(),
    abilities: () => this.workspace().abilities,
    delay: (value) => this.delay(value),
  });

  /**
   * Thinkers, AI permissions and AI actions — with the server's own gate: every curation write
   * is an admin act on the workspace, so a surface that forgets to gate a button fails here
   * rather than in production.
   */
  private readonly aiAdmin = createFakeAiAdmin({
    requireSession: () => this.requireSession(),
    abilities: () => this.workspace().abilities,
    delay: (value) => this.delay(value),
  });

  thinkers = this.aiAdmin.thinkers;
  capabilities = this.aiAdmin.capabilities;
  aiActions = this.aiAdmin.aiActions;

  /** Flow health, document tasks and nudges (W2) — same admin gate the server enforces. */
  private readonly ops = createFakeOps({
    requireSession: () => this.requireSession(),
    abilities: () => this.workspace().abilities,
    delay: (value) => this.delay(value),
  });

  health = this.ops.health;
  documentTasks = this.ops.documentTasks;
  nudges = this.ops.nudges;

  /**
   * W7 — Taskize and the notification publisher. The server gates NEITHER beyond
   * authentication, which is exactly why the fake gates both: the affordance IS the access
   * control here, so a surface that forgets it fails offline instead of leaking text or
   * broadcasting to the workspace.
   */
  private readonly intentsApi = createFakeIntents({
    requireSession: () => {
      this.requireSession();
    },
    canUpdate: (documentId) =>
      this.findDocument(documentId).abilities[ABILITY.documentUpdate] === true,
    canBroadcast: () => this.workspace().abilities[ABILITY.workspaceUpdate] === true,
    delay: (value) => this.delay(value),
  });

  intents = this.intentsApi.intents;

  /** W10 — stars (and, later in the wave, pins, share writes, permissions, templates). */
  private readonly libraryApi = createFakeLibrary({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
    documentFor: (id) => this.clone(this.findDocument(id)),
    collectionExists: (id) => this.collectionStore.some((collection) => collection.id === id),
    canShareDocument: (id) => this.findDocument(id).abilities[ABILITY.documentShare] === true,
    shareOrigin: "https://desk.example",
    allDocuments: () => this.documentStore.map((document) => this.clone(document)),
    viewerId: () => VIEWER.id,
    canManageCollection: (id) =>
      this.collectionStore.find((collection) => collection.id === id)?.abilities[
        ABILITY.collectionUpdate
      ] === true,
    userExists: (id) => this.memberStore.some((member) => member.id === id),
    groupExists: (id) => this.groupStore.some((group) => group.id === id),
    createDocument: async (input) => {
      const created = await this.documents.create({
        title: input.title,
        text: input.text,
        collectionId: input.collectionId ?? null,
      });
      if (input.template) {
        this.templateStore.add(created.id);
      }
      return created;
    },
    markAsTemplate: (id) => {
      const document = this.requireDocumentAbility(
        id,
        ABILITY.documentUpdate,
        "You cannot turn this document into a template",
      );
      this.templateStore.add(id);
      return this.clone(document);
    },
    templateIds: () => this.templateStore,
  });

  /**
   * W10 §9 — which documents are templates. A SET beside the document store rather than a field
   * on `Document`, because `template` is not projected onto slate's document model anywhere
   * else; the platform answers the question with `documents.list {template:true}`, and so does
   * this.
   */
  private templateStore = new Set<string>();

  stars = this.libraryApi.stars;
  pins = this.libraryApi.pins;
  shareLinks = this.libraryApi.shareLinks;
  exports = this.libraryApi.exports;
  searches = this.libraryApi.searches;
  attachments = this.libraryApi.attachments;

  /**
   * The four starter templates, clean-roomed from the C# controller (W7 §A4.2) — never from
   * Outline's template machinery. Idempotent by title, case-insensitively, exactly as the
   * server is: a second call returns the SAME ids rather than four more documents.
   */
  templates = {
    ...this.libraryApi.templates,
    ensureStarters: async (): Promise<Document[]> => {
      this.requireSession();
      if (!this.workspace().abilities[ABILITY.workspaceCreateDocument]) {
        throw new ForbiddenError("You cannot create documents in this workspace");
      }
      const created: Document[] = [];
      for (const starter of STARTER_TEMPLATES) {
        const existing = this.documentStore.find(
          (document) => document.title.toLowerCase() === starter.title.toLowerCase(),
        );
        if (existing) {
          // The server's starters are real templates (`doc.MarkAsTemplate()`), so a starter
          // must appear in `templates.list()` — including one that already existed.
          this.templateStore.add(existing.id);
          created.push(this.clone(existing));
          continue;
        }
        const now = new Date().toISOString();
        const document: Document = {
          id: `tpl-${starter.title
            .toLowerCase()
            .replace(/[^a-z]+/g, "-")
            .replace(/^-|-$/g, "")}`,
          title: starter.title,
          emoji: null,
          summary: FakeApi.firstProse(starter.text),
          text: starter.text,
          collectionId: null,
          parentDocumentId: null,
          createdAt: now,
          updatedAt: now,
          publishedAt: null,
          archivedAt: null,
          deletedAt: null,
          createdBy: ADA,
          views: 0,
          editorLane: "v1",
          abilities: {
            [ABILITY.documentUpdate]: true,
            [ABILITY.documentDelete]: true,
            [ABILITY.documentShare]: true,
            [ABILITY.documentArchive]: true,
            [ABILITY.documentRestore]: false,
          },
        };
        this.documentStore = [...this.documentStore, document];
        this.templateStore.add(document.id);
        created.push(this.clone(document));
      }
      return this.delay(created);
    },
  };

  /**
   * W5: projects, Members/Groups writes, and workspace governance. `members`/`groups` above
   * stay the read lane; this is the write half, and it shares the same `memberStore`/
   * `groupStore` those reads serve from, so an invite or a suspend shows up there immediately.
   */
  private readonly governanceApi = createFakeGovernance({
    requireSession: () => this.requireSession(),
    abilities: () => this.workspace().abilities,
    delay: (value) => this.delay(value),
    viewerId: () => VIEWER.id,
    members: () => this.memberStore,
    setMembers: (next) => {
      this.memberStore = next;
    },
    groups: () => this.groupStore,
    setGroups: (next) => {
      this.groupStore = next;
    },
    collections: () => this.collectionStore,
    setCollections: (next) => {
      this.collectionStore = next;
    },
  });

  projects = this.governanceApi.projects;
  people = this.governanceApi.people;
  governance = this.governanceApi.governance;

  /**
   * W4: connected editor windows, dispatch writes, grants, doc sync, conversations, wizard —
   * with the server's own refusals by their wire names, so D1 ("an Admin may watch a
   * colleague's window and may never drive it") and G6 ("admins never create or escalate a
   * grant") fail a test here rather than in somebody's filesystem.
   *
   * `dispatches` is the SAME array the automation namespace serves, so `ai-dispatch.get` and
   * `ai-dispatch.list` cannot describe the same row differently, and a cancel lands in both.
   */
  private readonly ideApi = createFakeIde({
    requireSession: () => this.requireSession(),
    abilities: () => this.workspace().abilities,
    delay: (value) => this.delay(value),
    viewerId: () => VIEWER.id,
    preferences: () => this.preferencesStore,
    dispatches: () => this.automation.dispatches.rows(),
  });

  ide = this.ideApi.ide;
  docSync = this.ideApi.docSync;
  wizard = this.ideApi.wizard;

  /**
   * W8 — the document AI layer. One namespace over two controllers, with the host document's
   * policy as the only gate (there are no abilities anywhere on that wire), and a SCRIPTED SSE
   * TAPE so the whole streaming path is provable with no backend at all.
   */
  private readonly conversationsApi = createFakeConversations({
    requireSession: () => {
      this.requireSession();
    },
    requireDocument: (documentId) => this.findDocument(documentId),
    canUpdate: (documentId) =>
      this.findDocument(documentId).abilities[ABILITY.documentUpdate] === true,
    canComment: (documentId) =>
      this.findDocument(documentId).abilities[ABILITY.documentComment] === true,
    delay: (value) => this.delay(value),
    viewerId: () => VIEWER.id,
    viewerName: () => VIEWER.name,
  });

  /** W4's remote-session store and W8's document AI layer share one namespace by design. */
  conversations = { ...this.ideApi.conversations, ...this.conversationsApi };

  /**
   * W8 S9/S10. The gates are the ones the SERVER does not have: a comment write needs
   * `documentComment`, and editing or deleting one needs to be yours — the platform checks
   * neither, so a client regression would otherwise pass every test here.
   */
  private readonly commentsApi = createFakeComments({
    requireSession: () => {
      this.requireSession();
    },
    requireDocument: (documentId) => this.findDocument(documentId),
    canComment: (documentId) =>
      this.findDocument(documentId).abilities[ABILITY.documentComment] === true,
    delay: (value) => this.delay(value),
    viewerId: () => VIEWER.id,
    viewerName: () => VIEWER.name,
  });

  comments = this.commentsApi.comments;
  documentChat = this.commentsApi.documentChat;

  /**
   * W6 — the v2 editing lane, selection AI, mentions and live widget values.
   *
   * `canUpdate` reads the SAME ability the real ACL reads, which is what makes the fake's
   * refusals real: a test that expects apply-in-place on a document the viewer cannot update
   * fails loudly here instead of passing with no backend behind it.
   */
  private readonly editorApi = createFakeEditor({
    requireSession: () => {
      this.requireSession();
    },
    findDocument: (id) => this.findDocument(id),
    documents: () => this.documentStore.map((d) => this.clone(d)),
    people: () => MEMBERS.map((m) => ({ id: m.id, name: m.name, avatarUrl: m.avatarUrl })),
    canUpdate: (documentId) =>
      this.findDocument(documentId).abilities[ABILITY.documentUpdate] === true,
    delay: (value) => this.delay(value),
  });

  editorV2 = this.editorApi.editorV2;
  selection = this.editorApi.selection;
  mentions = this.editorApi.mentions;
  quotes = this.editorApi.quotes;

  /**
   * The AI ledger as fixture data. The shapes mirror the platform's four-ledger union: the
   * pulse lane keeps measured and estimated tokens apart, one model carries a fallback rate
   * (`rateKnown: false`), the engines lane is statistical work with no LLM in it, and the
   * call feed includes failures — so every honesty affordance in the UI is provable offline.
   */
  ai = {
    usage: async (query: AiUsageQuery = {}): Promise<AiUsage> => {
      this.requireSession();
      return this.delay({
        windowDays: Math.min(Math.max(query.days ?? 30, 1), 365),
        totals: { calls: 1_958, llmTokens: 14_618_240, costMicros: 7_412_000, costUsd: 7.412 },
        pulse: {
          grain: "one row per model call (pulse_ai_provenance)",
          calls: 1_764,
          measuredTokens: 9_640_000,
          estimatedTokens: 1_310_000,
          costMicros: 2_190_000,
          byModel: [
            {
              model: "gemma4:26b",
              thinkerIds: ["lclgemma"],
              calls: 1_012,
              failures: 71,
              measuredTokens: 7_820_000,
              estimatedTokens: 0,
              durationMs: 40_530_000,
              costMicros: 1_564_000,
              rateKnown: true,
            },
            {
              model: "qwen2.5:7b-instruct",
              thinkerIds: ["lclqwen"],
              calls: 688,
              failures: 4,
              measuredTokens: 1_820_000,
              estimatedTokens: 1_290_000,
              durationMs: 9_870_000,
              costMicros: 466_000,
              // The fallback-rate row: the price is a visibility estimate, and says so.
              rateKnown: false,
            },
            {
              model: "fixture-model",
              thinkerIds: ["thinker-1"],
              calls: 64,
              failures: 0,
              measuredTokens: 0,
              estimatedTokens: 20_000,
              durationMs: 310_000,
              costMicros: 160_000,
              rateKnown: false,
            },
          ],
          byStage: [
            {
              stage: "entity-extraction",
              calls: 1_012,
              failures: 71,
              tokens: 7_820_000,
              models: ["gemma4:26b"],
            },
            {
              stage: "fact-rescan",
              calls: 688,
              failures: 4,
              tokens: 3_110_000,
              models: ["qwen2.5:7b-instruct"],
            },
            {
              stage: "sentinel",
              calls: 64,
              failures: 0,
              tokens: 20_000,
              models: ["fixture-model"],
            },
          ],
        },
        actions: {
          grain: "one row per IDE/flow command (ai_actions), four token categories, stored cost",
          calls: 194,
          inputTokens: 2_950_000,
          cacheWriteTokens: 410_000,
          cacheReadTokens: 720_000,
          outputTokens: 188_240,
          costMicros: 5_120_000,
          byModel: [
            {
              model: "fixture-sonnet",
              thinkerIds: ["reviewer"],
              calls: 182,
              inputTokens: 2_760_000,
              cacheWriteTokens: 410_000,
              cacheReadTokens: 720_000,
              outputTokens: 176_000,
              costMicros: 4_890_000,
              rateKnown: true,
            },
            {
              model: "fixture-haiku",
              thinkerIds: ["reviewer"],
              calls: 12,
              inputTokens: 190_000,
              cacheWriteTokens: 0,
              cacheReadTokens: 0,
              outputTokens: 12_240,
              costMicros: 230_000,
              rateKnown: true,
            },
          ],
        },
        billing: {
          grain: "one row per terminal run (billing_usage) — volume only, no model, no price",
          tokensIn: 3_360_000,
          tokensOut: 201_000,
          bySource: [
            { source: "flow", tokens: 2_940_000 },
            { source: "ide", tokens: 621_000 },
          ],
        },
        engines: {
          grain: "one row per causal discovery run (pulse_causal_runs) — statistical, no LLM",
          runs: 22,
          costMicros: 102_000,
          engines: [
            {
              engine: "lagged-ci",
              kind: "statistical — no LLM",
              runs: 14,
              failed: 1,
              durationMs: 152_000,
              costMicros: 76_000,
            },
            {
              engine: "hawkes",
              kind: "statistical — no LLM",
              runs: 8,
              failed: 0,
              durationMs: 52_000,
              costMicros: 26_000,
            },
          ],
        },
        byFlow: [
          { flow: "morning-brief", calls: 96, tokens: 1_840_000, costMicros: 2_710_000 },
          { flow: "release-notes", calls: 58, tokens: 1_260_000, costMicros: 1_890_000 },
          { flow: "weekly-audit", calls: 40, tokens: 480_000, costMicros: 520_000 },
        ],
        daily: Array.from({ length: 14 }, (_, index) => ({
          day: new Date(BASE_TIME - (13 - index) * 86_400_000).toISOString().slice(0, 10),
          calls: 90 + ((index * 37) % 120),
          tokens: 700_000 + ((index * 131) % 7) * 90_000,
          costMicros: 380_000 + ((index * 53) % 5) * 60_000,
        })),
        reading: [
          "four ledgers at four grains, reported side by side — no key joins them all.",
          "provenance rows are priced at the model's output rate over one token total; " +
            "estimated (chars/4) tokens are priced but reported apart from measured ones.",
          "statistical engine runs are priced at a flat placeholder rate for visibility, " +
            "not a vendor price.",
        ],
      });
    },

    calls: async (query: AiCallsQuery = {}): Promise<AiCallsPage> => {
      this.requireSession();
      const all: AiCall[] = [
        {
          at: hoursAgo(0.2),
          lane: "actions",
          what: "Write the notes",
          thinkerId: "reviewer",
          model: "fixture-sonnet",
          sessionId: "sess-4f21ab90",
          flow: "release-notes",
          target: null,
          tokensIn: 18_400,
          tokensOut: 1_260,
          tokensTotal: 19_660,
          tokensMeasured: true,
          durationMs: 9_400,
          costMicros: 61_000,
          outcome: "ok",
        },
        {
          at: hoursAgo(0.6),
          lane: "pulse",
          what: "entity-extraction",
          thinkerId: "lclgemma",
          model: "gemma4:26b",
          sessionId: "run-8c11d2e4",
          flow: null,
          target: "news-item:item-2041 (entities)",
          tokensIn: null,
          tokensOut: null,
          tokensTotal: 7_820,
          tokensMeasured: true,
          durationMs: 41_000,
          costMicros: 1_560,
          outcome: "ok",
        },
        {
          at: hoursAgo(1.1),
          lane: "pulse",
          what: "entity-extraction",
          thinkerId: "lclgemma",
          model: "gemma4:26b",
          sessionId: "run-8c11d2e4",
          flow: null,
          target: "news-item:item-2040 (entities)",
          tokensIn: null,
          tokensOut: null,
          tokensTotal: 0,
          tokensMeasured: false,
          durationMs: 120_000,
          costMicros: 0,
          // A failed call is a row, not a gap — the feed must show it.
          outcome: "timeout after 120s",
        },
        {
          at: hoursAgo(2.4),
          lane: "engines",
          what: "causal discovery · lagged-ci · 18 vars / 240 rows",
          thinkerId: "pulse-causal",
          model: "lagged-ci",
          sessionId: "causal-71b0c9aa",
          flow: null,
          target: "causal-run:causal-71b0c9aa",
          tokensIn: null,
          tokensOut: null,
          tokensTotal: null,
          tokensMeasured: false,
          durationMs: 11_200,
          costMicros: 5_600,
          outcome: "ok",
        },
        {
          at: hoursAgo(3.0),
          lane: "actions",
          what: "Rank what matters",
          thinkerId: "reviewer",
          model: "fixture-sonnet",
          sessionId: "sess-4f21ab90",
          flow: "morning-brief",
          target: null,
          tokensIn: 24_100,
          tokensOut: 980,
          tokensTotal: 25_080,
          tokensMeasured: true,
          durationMs: 12_600,
          costMicros: 78_000,
          outcome: "ok",
        },
        {
          at: hoursAgo(4.2),
          lane: "pulse",
          what: "fact-rescan",
          thinkerId: "lclqwen",
          model: "qwen2.5:7b-instruct",
          sessionId: null,
          flow: null,
          target: "subject:hormuz-oil-transit (facts)",
          tokensIn: null,
          tokensOut: null,
          tokensTotal: 2_140,
          tokensMeasured: false,
          durationMs: 6_800,
          costMicros: 420,
          outcome: "ok",
        },
        {
          at: hoursAgo(5.5),
          lane: "pulse",
          what: "sentinel",
          thinkerId: "thinker-1",
          model: "fixture-model",
          sessionId: "run-2b90ff13",
          flow: null,
          target: "proposal:prop-88 (verdict)",
          tokensIn: null,
          tokensOut: null,
          tokensTotal: 310,
          tokensMeasured: false,
          durationMs: 2_100,
          costMicros: 2_480,
          outcome: "refused: safety rail",
        },
        {
          at: hoursAgo(7.9),
          lane: "engines",
          what: "causal discovery · hawkes · 12 vars / 6774 events",
          thinkerId: "pulse-causal",
          model: "hawkes",
          sessionId: "causal-3d55e001",
          flow: null,
          target: "causal-run:causal-3d55e001",
          tokensIn: null,
          tokensOut: null,
          tokensTotal: null,
          tokensMeasured: false,
          durationMs: 6_500,
          costMicros: 3_250,
          outcome: "ok",
        },
      ];
      return this.delay({
        windowDays: Math.min(Math.max(query.days ?? 7, 1), 365),
        calls: all
          .filter((call) => (query.lane ? call.lane === query.lane : true))
          .slice(0, query.limit ?? 100),
        reading: [
          "pulse = one model call · actions = one IDE/flow command · engines = one " +
            "statistical discovery run (no LLM).",
        ],
      });
    },

    queueDigest: async (): Promise<QueueDigest> => {
      this.requireSession();
      return this.delay({
        transport: "queue" as const,
        payloadEncryption: false,
        backlog: {
          pendingArticles: 214,
          note: "articles of this team still awaiting extraction (the DB predicate; the queue holds the addressed slice of it).",
        },
        queue: {
          name: "knowit.pulse-digest.v1",
          messages: 37,
          consumers: 2,
          band: { min: 1, max: 4, scaleUpAt: 25 },
        },
        deadLetters: 3,
        drain: { lastHour: 54, lastDay: 812 },
      });
    },
  };

  /** CNPY-7/8 — the knowledge canopy. A small fixture tree carrying every label the page renders. */
  canopy = createFakeCanopy({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
  });

  /** Desk `/knowledge/debug` — a fixture pipeline with one of every state. */
  knowledgeDebug = createFakeKnowledgeDebug({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
  });

  /**
   * Workspace Knowledge — a GameDate-shaped workspace whose refreshes run and land as the
   * page reads. `knowledgeFixture` is the test handle: hold or land a run, switch an engine
   * off, or queue a 503 refusal.
   */
  readonly knowledgeFixture = createFakeKnowledge({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
    workspace: () => {
      const current = this.workspace();
      return { id: current.id, name: current.name };
    },
    canAct: () => this.workspace().abilities[ABILITY.workspaceUpdate] === true,
    viewerName: () => VIEWER.name,
  });
  knowledge = this.knowledgeFixture.api;

  /** W9 §A.1 — the wisdom graph. Self-contained fixture; no document store dependency. */
  wisdom = createFakeWisdom({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
  });

  /**
   * W9 §A.2 — merge, revision parts and restore. `canUpdateDocument` reads the SAME ability the
   * real ACL reads, so a test that expects `open` to refuse on a document the viewer cannot
   * update fails loudly here rather than passing with no backend behind it.
   */
  private readonly mergeApi = createFakeMerge({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
    viewerId: () => VIEWER.id,
    canUpdateDocument: (documentId) => {
      try {
        return this.findDocument(documentId).abilities[ABILITY.documentUpdate] === true;
      } catch {
        return false;
      }
    },
    documentExists: (documentId) => {
      try {
        this.findDocument(documentId);
        return true;
      } catch {
        return false;
      }
    },
  });

  merge = this.mergeApi;

  /** W9 §A.3 — share grants: management + the three anonymous `*.viaGrant` reads. */
  private readonly grantsApi = createFakeGrants({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
    canShareDocument: (documentId) => {
      try {
        return this.findDocument(documentId).abilities[ABILITY.documentShare] === true;
      } catch {
        return false;
      }
    },
    documentExists: (documentId) => {
      try {
        this.findDocument(documentId);
        return true;
      } catch {
        return false;
      }
    },
    documentFor: (documentId) => {
      try {
        const document = this.findDocument(documentId);
        // Authors are withheld server-side on a granted read — never carry `createdBy` here.
        return {
          id: document.id,
          title: document.title,
          text: document.text,
          emoji: document.emoji,
          updatedAt: document.updatedAt,
        };
      } catch {
        return null;
      }
    },
  });

  grants = this.grantsApi;

  /** W9 §A.4 — the owner's half of `profiles.*`. */
  profiles = createFakeProfiles({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
    viewerId: () => VIEWER.id,
  });

  /** W9 §A.5 — the inbox and preferences, gated exactly like the real ownership-checked REST
   * twins (§E-12): a notification that is not yours 404s, never 403s. */
  notifications = createFakeNotifications({
    requireSession: () => this.requireSession(),
    delay: (value) => this.delay(value),
    viewerId: () => VIEWER.id,
  });

  /** Deliberately does NOT call requireSession — these are the unauthenticated surfaces. */
  public = {
    share: async (shareId: string): Promise<{ share: Share; document: Document }> => {
      const share = this.shareStore.find((s) => s.id === shareId);
      if (!share) {
        throw new NotFoundError("This link is no longer available");
      }
      return this.delay({
        share: this.clone(share),
        document: this.clone(this.findDocument(share.documentId)),
      });
    },
    profile: async (slug: string): Promise<PublicProfileCv> => {
      const member = this.memberStore.find((m) => m.email.split("@")[0] === slug);
      if (!member) {
        throw new NotFoundError("No such profile");
      }
      return this.delay({
        id: member.id,
        userId: member.id,
        certified: false,
        // This legacy fixture profile has never sat the system interview.
        interview: null,
        slug,
        displayName: member.name,
        headline: "Fixture profile",
        bioShort: "Published notes and specs.",
        bioExtended: null,
        avatarUrl: member.avatarUrl,
        timezone: "UTC",
        availability: "ProjectBased",
        collaborationStyle: "Async",
        languages: ["en"],
        visibility: "Public",
        isOwner: false,
        skills: [
          {
            id: "sk-1",
            name: "TypeScript",
            level: "Expert",
            endorsementCount: 3,
            projectsUsedCount: 5,
          },
          {
            id: "sk-2",
            name: "Analytical engines",
            level: "Advanced",
            endorsementCount: 1,
            projectsUsedCount: 2,
          },
        ],
        portfolio: null,
        updatedAt: null,
      });
    },
  };

  /** Test/dev affordance — the shell only ever toasts about workspace switching (§15). */
  setCurrentWorkspace(id: string): void {
    this.currentWorkspaceId = id;
  }
}
